import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { applyBulletToggleToSelection, applySmartEnterAtSelection } from "../shared/utils/textareaLineBlockEdit";

/**
 * @typedef {{ toggleBullet: () => void, focus: () => void }} AiProjectBundleTextEditorHandle
 */

/**
 * Plain-text editor for AI Project Builder: monospace textarea, smart Enter for bullets.
 * Bullet control is usually placed in the page actions column; call `ref.current.toggleBullet()`.
 *
 * @param {{
 *   value: string,
 *   onChange: (next: string) => void,
 *   disabled?: boolean,
 *   id?: string,
 *   minRows?: number,
 *   'aria-label'?: string,
 * }} props
 */
const AiProjectBundleTextEditor = forwardRef(function AiProjectBundleTextEditor(
  { value, onChange, disabled = false, id, minRows = 10, "aria-label": ariaLabel },
  ref,
) {
  const taRef = useRef(null);
  const pendingSelectionRef = useRef(null);

  useLayoutEffect(() => {
    const p = pendingSelectionRef.current;
    if (!p) return;
    pendingSelectionRef.current = null;
    const el = taRef.current;
    if (!el || typeof el.setSelectionRange !== "function") return;
    const start = Math.max(0, Math.min(p.start, el.value.length));
    const end = Math.max(0, Math.min(p.end, el.value.length));
    el.focus();
    el.setSelectionRange(start, end);
  }, [value]);

  const runLineOp = useCallback(
    (fn) => {
      if (disabled) return;
      const el = taRef.current;
      if (!el) return;
      const { newText, newSelStart, newSelEnd } = fn(el.value, el.selectionStart, el.selectionEnd);
      pendingSelectionRef.current = { start: newSelStart, end: newSelEnd };
      onChange(newText);
    },
    [disabled, onChange],
  );

  useImperativeHandle(
    ref,
    () => ({
      toggleBullet: () => runLineOp(applyBulletToggleToSelection),
      focus: () => taRef.current?.focus(),
    }),
    [runLineOp],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      if (e.key !== "Enter" || e.shiftKey) return;
      if (e.nativeEvent.isComposing) return;
      const el = taRef.current;
      if (!el) return;
      const applied = applySmartEnterAtSelection(el.value, el.selectionStart, el.selectionEnd);
      if (!applied) return;
      e.preventDefault();
      pendingSelectionRef.current = {
        start: applied.newSelStart,
        end: applied.newSelEnd,
      };
      onChange(applied.newText);
    },
    [disabled, onChange],
  );

  return (
    <div className="ai-bundle-text-editor">
      <textarea
        ref={taRef}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={minRows}
        disabled={disabled}
        className="input input-full ai-bundle-text-editor__textarea"
        spellCheck
        aria-label={ariaLabel}
        style={{
          whiteSpace: "pre-wrap",
          tabSize: 2,
          MozTabSize: 2,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          fontSize: 13,
          lineHeight: 1.45,
        }}
      />
    </div>
  );
});

AiProjectBundleTextEditor.displayName = "AiProjectBundleTextEditor";

export default AiProjectBundleTextEditor;
